import { useState, useEffect } from "react";
import { Tv, Wifi, Play, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const Network = () => {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/whitelabel/showcase`)
      .then((r) => r.json())
      .then((data) => setStations(Array.isArray(data) ? data : []))
      .catch(() => setStations([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white">
      <SEO
        title="Live Video Showcase"
        description="Watch live video feeds from premium DJsandMCMedia™ stations. Experience the best DJs, MCs, and podcasters streaming right now."
      />
      <Header />

      <main className="max-w-6xl mx-auto px-6 py-20">
        {/* Hero */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 px-4 py-1.5 rounded-full text-purple-400 text-sm mb-6">
            <Tv className="w-4 h-4" /> Premium Stations
          </div>
          <h1 data-testid="showcase-heading" className="text-4xl sm:text-5xl font-bold mb-3">
            Live Video Showcase
          </h1>
          <p className="text-white/50 text-base max-w-lg mx-auto">
            Tune in to premium DJsandMCMedia&trade; stations. Experience the best DJs, MCs, and podcasters streaming right now.
          </p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : stations.length === 0 ? (
          <div className="text-center py-20">
            <Tv className="w-16 h-16 text-white/15 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white/50 mb-2">No premium stations yet</h2>
            <p className="text-white/30 text-sm max-w-md mx-auto mb-6">
              Premium stations will appear here. Get your own white-label player to be featured!
            </p>
            <Link to="/#pricing" className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-6 py-2.5 rounded-full text-sm font-medium transition" data-testid="showcase-cta">
              Get Premium
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="showcase-grid">
            {stations.map((station) => (
              <div
                key={station.slug}
                data-testid={`showcase-station-${station.slug}`}
                className="group bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 hover:border-purple-500/30 hover:bg-white/[0.05] transition-all text-center"
              >
                {/* Logo */}
                <div className="w-24 h-24 rounded-2xl mx-auto mb-4 overflow-hidden bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center shadow-lg shadow-purple-500/5">
                  {station.logo_url ? (
                    <img src={station.logo_url} alt={station.player_name} className="w-full h-full object-cover" />
                  ) : (
                    <Wifi className="w-10 h-10 text-purple-400" />
                  )}
                </div>

                {/* Station Info */}
                <h3 className="text-white font-semibold text-lg mb-1 truncate">{station.player_name || "Unnamed Station"}</h3>
                {station.tagline && (
                  <p className="text-white/40 text-sm mb-3 truncate">{station.tagline}</p>
                )}

                {/* LIVE Badge */}
                <div className="mb-4">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-green-500/15 text-green-400 animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> LIVE
                  </span>
                </div>

                {/* Listen Button */}
                <Link
                  to={`/player/${station.slug}`}
                  data-testid={`showcase-listen-${station.slug}`}
                  className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white font-semibold text-sm transition hover:opacity-90"
                  style={{ background: `linear-gradient(135deg, ${station.primary_color || '#8B5CF6'}, #ec4899)` }}
                >
                  <Play className="w-4 h-4" /> Listen Live
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-8 text-center" style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)", border: "1px solid rgba(139,92,246,0.15)" }}>
          <Crown className="w-10 h-10 text-purple-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold mb-2">Get Your Station Featured</h2>
          <p className="text-white/50 text-sm mb-4 max-w-md mx-auto">
            Upgrade to a premium white-label player and get your channel featured on this page.
          </p>
          <Link to="/#pricing" className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-6 py-2.5 rounded-full text-sm font-medium transition">
            View Plans
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Network;
